import { css, cx } from '@emotion/css';
import { Link } from 'react-router-dom';

// ─── Photo cards data ─────────────────────────────────────────────────────────
const ORANGE = '#f16602';

const content = [
  {
    id: 1,
    title: 'Events',
    subtitle: 'GRADE-SPECIFIC QUIZ PROGRAMS',
    line: 'Dive into curriculum-aligned quiz events covering Mathematics, General Knowledge, Space, Countries, Culture and more. Earn rewards with every quiz you clear.',
    img: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=700&q=80&fit=crop',
    icon: '🎯',
    link: '/openevents',
    accent: ORANGE,
  },
  {
    id: 2,
    title: 'Challenges',
    subtitle: 'CHALLENGE FRIENDS & STUDENTS',
    line: 'Challenge your friends or students with an extensive set of predefined questions across multiple grade levels. Create your own quiz and see how well they do.',
    img: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=700&q=80&fit=crop',
    icon: '⚡',
    link: '/assignments',
    accent: ORANGE,
  },
  {
    id: 3,
    title: 'Practices',
    subtitle: 'LEARN BY DOING',
    line: 'Practice grade-specific topics with our smart retry system. Questions you get wrong are flagged and repeated until you truly master the concept.',
    img: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=700&q=80&fit=crop',
    icon: '📚',
    link: '/practiceevents',
    accent: ORANGE,
  },
  {
    id: 4,
    title: 'Batches',
    subtitle: 'MANAGE YOUR CLASSROOM',
    line: 'Create batches of students, assign challenges, and track how well each student performs. The perfect tool for teachers and tutors.',
    img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=700&q=80&fit=crop',
    icon: '👥',
    link: '/batch',
    accent: ORANGE,
  },
];

// ─── Styles ───────────────────────────────────────────────────────────────────
const section = css`
  width: 100%;
  max-width: 1320px;
  margin: 0 auto 80px;
  padding: 0 20px;
`;

const heading = css`
  font-family: 'Inter', 'Poppins', sans-serif;
  font-size: 2rem;
  font-weight: 800;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: hsl(240, 15%, 15%);
  margin: 0 0 48px;

  @media (min-width: 768px) {
    font-size: 2.4rem;
  }
`;

const grid = css`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const card = css`
  position: relative;
  display: block;
  height: 420px;
  border-radius: 22px;
  overflow: hidden;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
  transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1),
              box-shadow 0.4s ease;

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 28px 60px rgba(0, 0, 0, 0.28);
    text-decoration: none;
  }

  .bg-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transition: transform 0.7s cubic-bezier(0.25, 1, 0.5, 1);
  }

  &:hover .bg-img {
    transform: scale(1.07);
  }

  .overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.0) 0%,
      rgba(30, 10, 0, 0.15) 30%,
      rgba(130, 45, 0, 0.72) 58%,
      rgba(80, 22, 0, 0.97) 100%
    );
    z-index: 1;
  }

  .icon-wrap {
    position: absolute;
    top: 18px;
    left: 18px;
    z-index: 3;
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.14);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.28);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    transition: background 0.3s;
  }

  &:hover .icon-wrap {
    background: rgba(255, 255, 255, 0.22);
  }

  .bottom {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2;
    padding: 1.5rem 1.5rem 1.4rem;
    display: flex;
    flex-direction: column;
  }

  .card-subtitle {
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin: 0 0 0.4rem 0;
    font-family: 'Inter', sans-serif;
  }

  .card-title {
    font-size: 1.85rem;
    font-weight: 800;
    color: #ffffff;
    text-transform: uppercase;
    letter-spacing: -0.01em;
    margin: 0 0 0.5rem 0;
    line-height: 1.05;
    font-family: 'Inter', sans-serif;
  }

  .card-desc {
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.72);
    line-height: 1.6;
    margin: 0 0 1rem 0;
    font-family: 'Inter', sans-serif;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .explore {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    font-family: 'Inter', sans-serif;
    transition: gap 0.3s ease;
  }

  &:hover .explore {
    gap: 13px;
  }

  .explore-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────
export default function Features() {
  return (
    <section className={section}>
      <h2 className={heading}>What We Offer</h2>
      <div className={grid}>
        {content.map((item) => (
          <Link
            key={item.id}
            to={item.link}
            className={cx(card)}
            style={{ '--accent': item.accent }}
          >
            {/* Full-bleed photo */}
            <img className="bg-img" src={item.img} alt={item.title} />

            {/* Gradient overlay */}
            <div className="overlay" />

            {/* Icon */}
            <div className="icon-wrap">{item.icon}</div>

            {/* Bottom text */}
            <div className="bottom">
              <p className="card-subtitle" style={{ color: item.accent }}>
                {item.subtitle}
              </p>
              <h3 className="card-title">{item.title}</h3>
              <p className="card-desc">{item.line}</p>
              <div className="explore" style={{ color: item.accent }}>
                <span
                  className="explore-dot"
                  style={{
                    background: item.accent,
                    boxShadow: `0 0 8px ${item.accent}99`,
                  }}
                />
                <span>Explore Now</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
